<script setup lang="ts">
import axios from 'axios';
import { computed, ref } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { fromJpg as fromJpgPage } from '@/routes/tools/pdf';
import fromJpgTool from '@/routes/tools/pdf/from-jpg';

const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.from_jpg.seo',
    fallbackTitleKey: 'tools.pdf.from_jpg.title',
    fallbackTitleLabel: 'JPG to PDF',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: fromJpgPage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.from_jpg.title',
                title: 'JPG to PDF',
                href: fromJpgPage.url(),
            },
        ],
    },
});

type Status = 'idle' | 'loading' | 'success' | 'error';

const files = ref<File[]>([]);
const status = ref<Status>('idle');
const statusMessage = ref('');
const dragStartIndex = ref<number | null>(null);

const selectionHint = computed(() => {
    const n = files.value.length;

    if (n === 0) {
        return '';
    }

    if (n === 1) {
        return t(
            'tools.pdf.ui.from_jpg_status_single',
            '1 image selected · Drag to reorder',
        );
    }

    return t(
        'tools.pdf.ui.from_jpg_status_multi',
        '{n} images selected · Drag to reorder',
    ).replace('{n}', String(n));
});

function onFilesSelected(newFiles: File[]): void {
    files.value = [...files.value, ...newFiles];
    status.value = 'idle';
}

function removeFile(index: number): void {
    files.value.splice(index, 1);
}

function onDragStart(index: number): void {
    dragStartIndex.value = index;
}

function onDragOver(event: DragEvent): void {
    event.preventDefault();
}

function onDrop(index: number): void {
    if (dragStartIndex.value === null || dragStartIndex.value === index) {
        dragStartIndex.value = null;

        return;
    }

    const copy = [...files.value];
    const [moved] = copy.splice(dragStartIndex.value, 1);
    copy.splice(index, 0, moved);
    files.value = copy;
    dragStartIndex.value = null;
}

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        const text = await (e.response.data as Blob).text();

        try {
            const parsed = JSON.parse(text);
            const messages = Object.values(parsed.errors ?? {}).flat();

            return (
                (messages[0] as string) ||
                t('tools.pdf.ui.conversion_failed', 'Conversion failed.')
            );
        } catch {
            return t('tools.pdf.ui.conversion_failed', 'Conversion failed.');
        }
    }

    return t('tools.pdf.ui.conversion_failed', 'Conversion failed.');
}

async function convert(): Promise<void> {
    if (files.value.length === 0 || status.value === 'loading') {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t(
        'tools.pdf.ui.converting_images_to_pdf',
        'Converting images to PDF...',
    );

    const formData = new FormData();
    files.value.forEach((file) => formData.append('files[]', file));

    try {
        const response = await axios.post(fromJpgTool.submit.url(), formData, {
            responseType: 'blob',
        });

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'images.pdf';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.pdf_created_success',
            'PDF created successfully!',
        );
        files.value = [];
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-from-jpg" />

    <ToolLayout
        :title="t('tools.pdf.from_jpg.title', 'JPG to PDF')"
        :subtitle="t('tools.pdf.from_jpg.description', '')"
    >
        <div class="space-y-4">
            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <DropZone
                    accept="image/jpeg,image/png"
                    :multiple="true"
                    :label="
                        t(
                            'tools.pdf.ui.drop_images_here',
                            'Click or drag images here',
                        )
                    "
                    :sublabel="
                        t(
                            'tools.pdf.ui.from_jpg_sublabel',
                            'JPG and PNG files · each image becomes one page',
                        )
                    "
                    @update:model-value="onFilesSelected"
                />

                <div v-if="files.length > 0" class="mt-4 space-y-2">
                    <div
                        v-for="(file, index) in files"
                        :key="file.name + index"
                        draggable="true"
                        @dragstart="onDragStart(index)"
                        @dragover="onDragOver"
                        @drop.prevent="onDrop(index)"
                    >
                        <FileCard
                            :file="file"
                            :show-drag-handle="true"
                            @remove="removeFile(index)"
                        />
                    </div>
                </div>

                <p
                    v-if="files.length > 0"
                    class="mt-2 text-xs text-gray-500 dark:text-gray-400"
                >
                    {{ selectionHint }}
                </p>
            </div>

            <Button
                class="w-full bg-brand-600 text-white hover:bg-brand-700"
                :disabled="files.length === 0 || status === 'loading'"
                @click="convert"
            >
                {{ t('tools.sections.to_pdf', 'Convert to PDF') }}
            </Button>

            <StatusMessage :status="status" :message="statusMessage" />
        </div>
    </ToolLayout>
</template>
